// TEMPORARY FEATURE: drawn overlay images. Self-contained, see overlay-drawing-launcher.component.ts.

import {afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, signal, viewChild} from "@angular/core"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog"
import {Vector2} from "@cm/math"
import type {Vector2Like} from "@cm/math"
import {createCurveDrawingCurve, rasterizeCurveDrawing, sampleCurveDrawingStroke} from "@cm/template-nodes"
import type {CurveDrawingImageParameters} from "@cm/template-nodes"
import {SplineCurveComponent} from "@app/common/components/curves/spline-curve/spline-curve.component"
import {
    hexToRgb,
    resizeCurveDrawing,
    rgbToHex,
} from "@template-editor/components/inspectors/mattress-inspector/components/overlay-drawing/overlay-drawing-helpers"

export type OverlayDrawingDialogData = {
    title: string
    parameters: CurveDrawingImageParameters
    // set when the drawing area is taken from the mattress, so width and height are not editable
    sizeFromMattress: boolean
    // drawings of other inputs the curve can be copied from, stretched to this drawing area
    sources: {label: string; parameters: CurveDrawingImageParameters}[]
    apply: (parameters: CurveDrawingImageParameters) => void
}

type RulerTick = {posPx: number; major: boolean; label: string}

const minorTickCm = 1
const minorTicksPerMajor = 5
// room for the label of the last top ruler tick, which is drawn to the right of its tick
const rulerLabelOverhangPx = 24
const insertSamplesPerSegment = 50
const insertTolerancePx = 8
const dragTolerancePx = 4

function formatCm(value: number) {
    return `${parseFloat(value.toFixed(2))}`
}

function rulerTicks(lengthCm: number, toPx: (valueCm: number) => number): RulerTick[] {
    const count = Math.floor(lengthCm / minorTickCm + 1e-6)
    const ticks: RulerTick[] = []
    for (let i = 0; i <= count; i++) {
        const valueCm = i * minorTickCm
        ticks.push({posPx: toPx(valueCm), major: i % minorTicksPerMajor === 0, label: formatCm(valueCm)})
    }
    return ticks
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-overlay-drawing-dialog",
    templateUrl: "./overlay-drawing-dialog.component.html",
    styleUrl: "./overlay-drawing-dialog.component.scss",
    imports: [MatDialogModule, MatButtonModule, SplineCurveComponent],
})
export class OverlayDrawingDialogComponent {
    readonly data = inject<OverlayDrawingDialogData>(MAT_DIALOG_DATA)
    private readonly dialogRef = inject(MatDialogRef<OverlayDrawingDialogComponent>)

    private readonly $previewCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>("previewCanvas")
    private readonly $canvasCell = viewChild.required<ElementRef<HTMLElement>>("canvasCell")
    private readonly $canvasArea = viewChild.required<ElementRef<HTMLElement>>("canvasArea")

    readonly topRulerHeightPx = 24
    readonly leftRulerWidthPx = 40
    readonly controlPointRadiusPx = 6
    readonly controlPointColor = "#dc2626"

    readonly $widthCm = signal(this.data.parameters.widthCm)
    readonly $heightCm = signal(this.data.parameters.heightCm)
    readonly $strokeWidthCm = signal(this.data.parameters.strokeWidthCm)
    readonly $color = signal(this.data.parameters.color)
    // a single curve for now, older drawings with more strokes keep only the first one
    readonly $controlPoints = signal<[number, number][]>(this.data.parameters.strokes[0]?.controlPoints ?? [])

    readonly $selectedControlPoint = signal<number | undefined>(undefined)
    readonly $hoveredControlPoint = signal<number | undefined>(undefined)

    // cursor position in canvas pixels (origin top left), undefined while the pointer is outside the canvas
    readonly $hoverPx = signal<Vector2Like | undefined>(undefined)

    // size of the area the canvas and its rulers have to fit into, measured once rendered
    private readonly $availableSizePx = signal({width: 0, height: 0})

    readonly $colorHex = computed(() => rgbToHex(this.$color()))

    readonly $canvasSizePx = computed(() => {
        const available = this.$availableSizePx()
        const scale = Math.min(
            (available.width - this.leftRulerWidthPx - rulerLabelOverhangPx) / this.$widthCm(),
            (available.height - this.topRulerHeightPx) / this.$heightCm(),
        )
        return {width: Math.max(1, Math.round(this.$widthCm() * scale)), height: Math.max(1, Math.round(this.$heightCm() * scale))}
    })

    // width and height are rounded separately, so each axis gets its own scale
    readonly $pxPerCm = computed(() => ({x: this.$canvasSizePx().width / this.$widthCm(), y: this.$canvasSizePx().height / this.$heightCm()}))

    readonly $rulerTicks = computed(() => {
        const pxPerCm = this.$pxPerCm()
        const heightCm = this.$heightCm()
        return {
            x: rulerTicks(this.$widthCm(), (valueCm) => valueCm * pxPerCm.x),
            y: rulerTicks(heightCm, (valueCm) => (heightCm - valueCm) * pxPerCm.y),
        }
    })

    readonly $hoverCm = computed(() => {
        const hoverPx = this.$hoverPx()
        if (!hoverPx) return undefined
        const pxPerCm = this.$pxPerCm()
        return {x: (hoverPx.x / pxPerCm.x).toFixed(1), y: (this.$heightCm() - hoverPx.y / pxPerCm.y).toFixed(1)}
    })

    readonly $parameters = computed<CurveDrawingImageParameters>(() => {
        const controlPoints = this.$controlPoints()
        return {
            ...this.data.parameters,
            widthCm: this.$widthCm(),
            heightCm: this.$heightCm(),
            strokeWidthCm: this.$strokeWidthCm(),
            color: this.$color(),
            strokes: controlPoints.length > 0 ? [{controlPoints}] : [],
        }
    })

    readonly $splineControlPoints = computed(() => this.$controlPoints().map((point) => this.toSpline(point)))

    readonly $splineCurvePoints = computed(() =>
        sampleCurveDrawingStroke(this.$controlPoints(), Math.max(this.$widthCm(), this.$heightCm()) / 500).map((point) => ({
            position: this.toSpline(point),
            color: "#16a34a",
        })),
    )

    // distance the "Shift" button moves the curve by, negative values move it down
    readonly $shiftCm = signal(0.5)

    readonly $selectedControlPointCm = computed(() => {
        const pointId = this.$selectedControlPoint()
        const point = pointId === undefined ? undefined : this.$controlPoints()[pointId]
        return point ? {x: formatCm(point[0]), y: formatCm(point[1])} : undefined
    })

    // set when the mouse moved between press and release, so the release is not treated as a click that adds a point
    private clickWasDrag = false

    constructor() {
        effect(() => this.updatePreview())

        const destroyRef = inject(DestroyRef)
        afterNextRender(() => {
            const canvasArea = this.$canvasArea().nativeElement
            const resizeObserver = new ResizeObserver(() => this.$availableSizePx.set({width: canvasArea.clientWidth, height: canvasArea.clientHeight}))
            resizeObserver.observe(canvasArea)
            destroyRef.onDestroy(() => resizeObserver.disconnect())

            // SplineCurveComponent stops the propagation of mousedown, so the press position is recorded in the capture phase
            const canvasCell = this.$canvasCell().nativeElement
            let pressedAt: {x: number; y: number} | undefined = undefined
            canvasCell.addEventListener("mousedown", (event) => (pressedAt = {x: event.clientX, y: event.clientY}), {capture: true})
            canvasCell.addEventListener(
                "click",
                (event) => {
                    this.clickWasDrag = pressedAt !== undefined && Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y) > dragTolerancePx
                },
                {capture: true},
            )
        })
    }

    private toSpline([x, y]: [number, number]): Vector2Like {
        return {x: x / this.$widthCm(), y: y / this.$heightCm()}
    }

    private fromSpline(point: Vector2Like): Vector2 {
        return new Vector2(point.x * this.$widthCm(), point.y * this.$heightCm())
    }

    private updatePreview() {
        const {width, height} = this.$canvasSizePx()
        const image = rasterizeCurveDrawing({...this.$parameters(), resolution: Math.max(width, height)})
        const canvas = this.$previewCanvas().nativeElement
        canvas.width = image.width
        canvas.height = image.height
        canvas.getContext("2d")?.putImageData(new ImageData(new Uint8ClampedArray(image.data), image.width, image.height), 0, 0)
    }

    handleCanvasMouseMove(event: MouseEvent) {
        const rect = this.$canvasCell().nativeElement.getBoundingClientRect()
        const {width, height} = this.$canvasSizePx()
        this.$hoverPx.set({
            x: Math.min(width, Math.max(0, event.clientX - rect.left)),
            y: Math.min(height, Math.max(0, event.clientY - rect.top)),
        })
    }

    handleCanvasMouseLeave() {
        this.$hoverPx.set(undefined)
    }

    // a mouseup after dragging a control point bubbles up as a background click, which must not append a point
    private pressedControlPoint = false

    handleBeginControlPointDrag() {
        this.pressedControlPoint = true
    }

    handleBackgroundMouseDown() {
        this.pressedControlPoint = false
    }

    handleBackgroundClicked(point: Vector2Like) {
        if (this.pressedControlPoint || this.clickWasDrag) return
        const clicked = this.fromSpline(point)

        // the clickable path of the curve is only a few pixels wide, so clicks right beside it still insert
        const controlPoints = this.$controlPoints()
        if (controlPoints.length >= 2) {
            const nearest = this.nearestCurveParameter(controlPoints, clicked)
            const thresholdCm = insertTolerancePx / Math.min(this.$pxPerCm().x, this.$pxPerCm().y)
            // near the curve ends, the click extends the curve instead
            if (nearest.distance < thresholdCm && nearest.t > 0 && nearest.t < 1) {
                this.insertControlPoint(controlPoints, nearest.t, clicked)
                return
            }
        }

        this.$controlPoints.update((points) => [...points, [clicked.x, clicked.y]])
        this.$selectedControlPoint.set(this.$controlPoints().length - 1)
    }

    handleSplineClicked(point: Vector2Like) {
        const controlPoints = this.$controlPoints()
        if (this.clickWasDrag || controlPoints.length < 2) return
        const clicked = this.fromSpline(point)
        this.insertControlPoint(controlPoints, this.nearestCurveParameter(controlPoints, clicked).t, clicked)
    }

    // getPoint maps t linearly onto the segments (unlike getSpacedPoints), so the segment index follows directly from t
    private nearestCurveParameter(controlPoints: [number, number][], position: Vector2) {
        const curve = createCurveDrawingCurve(controlPoints)
        const samplesCount = (controlPoints.length - 1) * insertSamplesPerSegment
        let closestT = 0
        let closestDistance = Infinity
        for (let i = 0; i <= samplesCount; i++) {
            const t = i / samplesCount
            const sample = curve.getPoint(t)
            const distance = position.distance(new Vector2(sample.x, sample.y))
            if (distance < closestDistance) {
                closestT = t
                closestDistance = distance
            }
        }
        return {t: closestT, distance: closestDistance}
    }

    private insertControlPoint(controlPoints: [number, number][], t: number, position: Vector2) {
        const segmentsCount = controlPoints.length - 1
        const insertionIndex = Math.min(Math.floor(t * segmentsCount), segmentsCount - 1) + 1
        this.$controlPoints.set([...controlPoints.slice(0, insertionIndex), [position.x, position.y], ...controlPoints.slice(insertionIndex)])
        this.$selectedControlPoint.set(insertionIndex)
    }

    handleControlPointChanged(pointId: number, point: Vector2Like) {
        const position = this.fromSpline(point)
        this.$controlPoints.update((controlPoints) => controlPoints.map((controlPoint, index) => (index === pointId ? [position.x, position.y] : controlPoint)))
    }

    deleteSelectedControlPoint() {
        const pointId = this.$selectedControlPoint()
        if (pointId === undefined) return
        this.$controlPoints.update((controlPoints) => controlPoints.filter((_, index) => index !== pointId))
        this.$selectedControlPoint.set(undefined)
    }

    clearCurve() {
        this.$controlPoints.set([])
        this.$selectedControlPoint.set(undefined)
    }

    setShift(event: Event) {
        const input = event.target as HTMLInputElement
        const value = parseFloat(input.value)
        if (Number.isFinite(value)) this.$shiftCm.set(value)
        else input.value = `${this.$shiftCm()}`
    }

    // the whole curve is shifted, but never beyond the top or bottom edge of the drawing area
    shiftCurve() {
        const controlPoints = this.$controlPoints()
        if (controlPoints.length === 0) return
        const ys = controlPoints.map(([, y]) => y)
        // clamped only in the shift direction, so points already outside the area never make it move the other way
        const shift = this.$shiftCm()
        const offset = shift > 0 ? Math.min(shift, Math.max(0, this.$heightCm() - Math.max(...ys))) : Math.max(shift, Math.min(0, -Math.min(...ys)))
        this.$controlPoints.set(controlPoints.map(([x, y]) => [x, y + offset]))
    }

    // mirrored at the center of the drawing area, so the curve stays inside it
    mirrorCurve(axis: "x" | "y") {
        const widthCm = this.$widthCm()
        const heightCm = this.$heightCm()
        this.$controlPoints.update((controlPoints) => controlPoints.map(([x, y]) => (axis === "x" ? [widthCm - x, y] : [x, heightCm - y])))
    }

    readonly $copySourceIndex = signal(0)

    setCopySource(event: Event) {
        this.$copySourceIndex.set(parseInt((event.target as HTMLSelectElement).value))
    }

    // the source is stretched like a drawing whose face was resized, its stroke width and color are taken over as well
    copyFromSource() {
        const source = this.data.sources[this.$copySourceIndex()]
        if (!source) return
        const parameters = resizeCurveDrawing(source.parameters, this.$widthCm(), this.$heightCm())
        this.$controlPoints.set(parameters.strokes[0]?.controlPoints ?? [])
        this.$strokeWidthCm.set(parameters.strokeWidthCm)
        this.$color.set(parameters.color)
        this.$selectedControlPoint.set(undefined)
    }

    // change fires on blur, which happens after a click on the canvas may already have changed the selection,
    // so the edited point is remembered when the input gets focus
    private editedControlPoint: number | undefined = undefined

    handleControlPointCoordinateFocus() {
        this.editedControlPoint = this.$selectedControlPoint()
    }

    setSelectedControlPointCoordinate(axis: "x" | "y", event: Event) {
        const input = event.target as HTMLInputElement
        const value = parseFloat(input.value)
        const pointId = this.editedControlPoint
        if (Number.isFinite(value) && pointId !== undefined && pointId < this.$controlPoints().length) {
            const coordinate = Math.min(Math.max(value, 0), axis === "x" ? this.$widthCm() : this.$heightCm())
            this.$controlPoints.update((controlPoints) =>
                controlPoints.map(([x, y], index) => (index !== pointId ? [x, y] : axis === "x" ? [coordinate, y] : [x, coordinate])),
            )
        }
        // the bound value does not change when the input is clamped away, so the input is reset explicitly
        input.value = this.$selectedControlPointCm()?.[axis] ?? ""
    }

    setPositiveNumber(target: "width" | "height" | "strokeWidth", event: Event) {
        const input = event.target as HTMLInputElement
        const value = parseFloat(input.value)
        const $target = target === "width" ? this.$widthCm : target === "height" ? this.$heightCm : this.$strokeWidthCm
        if (Number.isFinite(value) && value > 0) $target.set(value)
        // invalid values leave the bound value unchanged, so the input is reset explicitly
        else input.value = `${$target()}`
    }

    setColor(event: Event) {
        this.$color.set(hexToRgb((event.target as HTMLInputElement).value))
    }

    apply() {
        this.data.apply(this.$parameters())
    }

    applyAndClose() {
        this.apply()
        this.dialogRef.close()
    }

    cancel() {
        this.dialogRef.close()
    }
}
